import React, { useEffect } from 'react';
import { ArrowRight, Check, MessageCircle, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from './seo/SEO';

const goldButton = 'inline-flex items-center gap-2 rounded-[3px] bg-[#dfad62] px-6 py-3 text-xs font-semibold text-[#101d29] transition hover:bg-[#edc17d]';
const eyebrow = 'flex items-center gap-3 text-[10px] font-bold uppercase tracking-[.19em] text-[#15283a]';
const line = <span className="h-px w-11 bg-[#d39c4c]" />;

const digitalMarketingPlans = [
  {
    name: 'Starter Local Visibility',
    badge: 'Local Businesses & Clinics',
    price: '₹8,999',
    period: '/ month',
    desc: 'Dominate your local neighborhood, capture nearby customers on Google Maps, and establish consistent social presence.',
    popular: false,
    benefits: [
      'Google Business Profile (GBP) complete 360° optimization',
      'Local Google Map Pack ranking boost & citation audit',
      '8 High-impact custom branded social media graphics / month',
      'Targeted local Meta Ads campaign setup & monitoring',
      'Review generation strategy & reputation management',
      'Monthly transparent ROI & ranking performance report',
    ],
  },
  {
    name: 'Lead Generation & Growth',
    badge: 'High ROI & Leads',
    price: '₹18,999',
    period: '/ month',
    desc: 'Data-driven paid ads and conversion funnels designed to deliver consistent, high-intent buyer inquiries and sales.',
    popular: true,
    benefits: [
      'Full Google Search & Call Ads campaign management',
      'High-converting Meta (Facebook & Instagram) lead funnels',
      '16 Professional creatives + 4 engaging video reels / month',
      'Landing page copy & conversion rate optimization',
      'Pixel, Google Analytics 4, and conversion API tracking',
      'Weekly ad budget optimization & ROAS scaling audits',
      'Dedicated marketing strategist & WhatsApp support',
    ],
  },
  {
    name: 'Omnichannel Market Leader',
    badge: 'Brands & Expanding Enterprises',
    price: '₹36,999',
    period: '/ month',
    desc: 'Aggressive multi-channel dominance combining paid media, technical SEO, content marketing, and brand positioning.',
    popular: false,
    benefits: [
      'Cross-platform ads: Google, YouTube, Meta & LinkedIn',
      'Comprehensive Technical & Content SEO for top organic ranks',
      '24 Premium creatives + 8 viral short-form video reels',
      'WhatsApp broadcast & email marketing automated funnels',
      'Competitor ad intelligence & market positioning audit',
      'Bi-weekly strategy consultation & executive dashboard',
      'Dedicated performance marketing team & senior strategist',
    ],
  },
];

export default function DigitalMarketingPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const whatsappNumber = '918610452001';

  return (
    <div className="bg-[#fdf9f2] font-sans text-[#152638]">
      <SEO title="Digital Marketing | GlobalEPRC" description="Build brand awareness, attract the right audience and grow your business with result-driven digital marketing strategies." path="/digital-marketing" />
      <main>
        {/* HERO SECTION */}
        <section className="relative isolate flex min-h-[480px] items-center overflow-hidden bg-[#f7f0e5] pt-[80px] sm:min-h-[520px]">
          <img src="/dm-1.png" alt="Digital marketing performance dashboard in a bright workspace" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="mx-auto w-full max-w-[1200px] px-7 py-12 md:px-10">
            <div className="max-w-[500px]">
              <p className={`${eyebrow} text-[11px]`}>Digital Marketing {line}</p>
              <h1 className="mt-5 font-serif text-[46px] leading-[1.06] tracking-[-.035em] text-[#102437] sm:text-[56px]">Strategies<br />That Drive Real<br />Business Growth</h1>
              <p className="mt-5 max-w-[430px] text-base leading-[1.55] text-slate-700">We help you reach the right audience, build brand awareness and increase engagement through result-driven digital marketing strategies.</p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <a href="#pricing" className={`${goldButton} px-6 py-3.5 text-sm`}>View Packages &amp; Pricing <ArrowRight className="h-4 w-4" /></a>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-[3px] border border-slate-300 bg-white/80 px-6 py-3.5 text-sm font-semibold text-slate-900 transition hover:bg-white">Discuss Your Goals</Link>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGES & PRICING */}
        <section id="pricing" className="border-t border-[#ede2d2] bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
            
            {/* Banner linking back to Top Software Pricing (G-Track 360 & Billing) */}
            <div className="mb-12 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50/50 to-amber-50/50 p-6 sm:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                    All-in-One Software Products
                  </span>
                  <h3 className="mt-2 text-xl font-black text-slate-900 sm:text-2xl">
                    Looking for Ready-to-Use Software Pricing?
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    Explore transparent annual &amp; lifetime plans for <strong className="text-slate-900">G-Track 360 HRM Software</strong>, <strong className="text-slate-900">G-Track POS Billing Software</strong>, and Biometric / RFID Attendance Terminals.
                  </p>
                </div>
                <Link
                  to="/pricing"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-700 active:scale-95"
                >
                  View Full Software Pricing Plans <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Section Header */}
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#bd8741]">
                ROI-Driven Growth Plans
              </p>
              <h2 className="mt-2 font-serif text-3xl font-black text-slate-950 sm:text-4xl lg:text-5xl">
                Digital Marketing Pricing &amp; Benefits
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                Transparent monthly marketing packages tailored to your scale — complete with high-converting creatives, ads management, and verified lead reporting.
              </p>
            </div>

            {/* Pricing Cards Grid */}
            <div className="mt-12 grid gap-8 pt-4 lg:grid-cols-3 lg:items-stretch">
              {digitalMarketingPlans.map((plan) => {
                const whatsappMsg = encodeURIComponent(
                  `Hello GlobalEPRC, I am interested in the "${plan.name}" plan for Digital Marketing (${plan.price}). Please share details and audit our business presence.`
                );
                const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMsg}`;

                return (
                  <div
                    key={plan.name}
                    className={`relative flex flex-col justify-between rounded-3xl border transition-all duration-300 p-7 sm:p-8 ${
                      plan.popular
                        ? 'border-blue-600 bg-white shadow-xl shadow-blue-500/10 ring-2 ring-blue-600 pt-9 sm:pt-10'
                        : 'border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white hover:shadow-lg'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                        <span className="whitespace-nowrap rounded-full bg-blue-600 px-4 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-md">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div>
                      {/* Category Badge Row - Identical height on all 3 cards */}
                      <div className="flex items-center min-h-[28px]">
                        <span className={`inline-block rounded-md px-2.5 py-1 text-[11px] font-bold ${
                          plan.popular
                            ? 'bg-blue-50 text-blue-700 border border-blue-200/80'
                            : 'bg-slate-200/80 text-slate-700'
                        }`}>
                          {plan.badge}
                        </span>
                      </div>

                      {/* Title Row - Identical height on all 3 cards */}
                      <h3 className="mt-3 text-xl font-black text-slate-900 min-h-[32px] sm:min-h-[56px] flex items-center">
                        {plan.name}
                      </h3>

                      {/* Description - Identical height on all 3 cards */}
                      <p className="mt-2 text-xs leading-relaxed text-slate-600 min-h-[48px]">
                        {plan.desc}
                      </p>

                      <div className="mt-6 border-y border-slate-200/80 py-4">
                        <div className="flex items-baseline gap-2">
                          <span className="font-serif text-4xl font-black text-slate-950 sm:text-5xl">
                            {plan.price}
                          </span>
                          <span className="text-xs font-semibold text-slate-500">
                            {plan.period}
                          </span>
                        </div>
                      </div>

                      {/* Benefits List */}
                      <div className="mt-6">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-900">
                          Included Benefits &amp; Deliverables:
                        </p>
                        <ul className="mt-3 space-y-3">
                          {plan.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 leading-snug">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Call to Actions */}
                    <div className="mt-8 space-y-2.5 pt-6 border-t border-slate-200/70">
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-xs font-bold transition active:scale-95 ${
                          plan.popular
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                            : 'bg-slate-950 text-white hover:bg-slate-800'
                        }`}
                      >
                        <MessageCircle className="h-4 w-4 text-emerald-300" />
                        Get Started via WhatsApp
                      </a>

                      <Link
                        to="/contact"
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition"
                      >
                        Request Custom Marketing Audit <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Support & Guarantee Note */}
            <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center sm:flex-row sm:text-left">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Need Custom Lead Volumes or Corporate Retainers?</h4>
                  <p className="text-xs text-slate-600">We manage multi-lakh ad spends with dedicated weekly ROAS audits and creative production.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/918610452001?text=Hi%20GlobalEPRC,%20I%20would%20like%20to%20audit%20and%20scale%20our%20digital%20marketing."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 transition"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp: +91 8610452001
                </a>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-800 hover:bg-slate-100 transition"
                >
                  Back to Main Pricing <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

          </div>
        </section>
      </main>
    </div>
  );
}